import { motion } from "framer-motion";
import { Linkedin, Twitter, Github, Youtube, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useSmartNav } from "@/lib/scroll";

const NAV = [
    { label: "About", target: "#about" },
    { label: "Capabilities", target: "#capabilities" },
    { label: "AI", target: "#ai" },
    { label: "Services", target: "/services" },
    { label: "Partners", target: "/partners" },
    { label: "Product", target: "/products/pqc-readiness" },
    { label: "Careers", target: "/careers" },
    { label: "FAQ", target: "#faq" },
    { label: "Contact", target: "#contact" },
];

const SOCIALS = [
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com" },
    { icon: Twitter, label: "X", href: "https://x.com" },
    { icon: Github, label: "GitHub", href: "https://github.com" },
    { icon: Youtube, label: "YouTube", href: "https://youtube.com" },
];

const WORD = "SYNTERA".split("");

export const Footer = () => {
    const go = useSmartNav();

    return (
        <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/[0.06] bg-[#05070B]">
            <div className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8">
                <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr_1fr]">
                    <div>
                        <Logo />
                        <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
                            At Syntera, we nurture a culture of transparency, curiosity and
                            continuous learning, engineering the autonomous enterprise from
                            Bengaluru to the world.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-all duration-300 hover:border-volt/60 hover:text-volt hover:shadow-[0_0_20px_rgba(0,240,255,0.2)]"
                                >
                                    <s.icon size={16} />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h4 className="mb-5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-volt">
                            Useful Links
                        </h4>
                        <ul className="space-y-3">
                            {NAV.map((n) => (
                                <li key={n.target}>
                                    <button
                                        data-testid={`footer-link-${n.label.toLowerCase()}`}
                                        onClick={() => go(n.target)}
                                        className="group flex items-center gap-1.5 text-sm text-slate-400 transition-colors duration-300 hover:text-white"
                                    >
                                        {n.label}
                                        <ArrowUpRight size={12} className="opacity-0 transition-all duration-300 group-hover:opacity-100" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="mb-5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-volt">
                            Contact Us
                        </h4>
                        <ul className="space-y-3 text-sm text-slate-400" data-testid="footer-contact-info">
                            <li>Bengaluru India</li>
                            <li>
                                <a href="mailto:info@synteratechnologies.com" className="transition-colors hover:text-volt" data-testid="footer-email-link">
                                    info@synteratechnologies.com
                                </a>
                            </li>
                            <li>
                                <a href="tel:+919908333515" className="transition-colors hover:text-volt" data-testid="footer-phone-link">
                                    +91 99083 33515
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 select-none overflow-hidden" aria-hidden="true">
                    <div className="flex justify-center font-syne text-[16.5vw] font-extrabold leading-none tracking-tight lg:text-[11rem]">
                        {WORD.map((letter, i) => (
                            <motion.span
                                key={i}
                                initial={{ y: "60%", opacity: 0 }}
                                whileInView={{ y: "0%", opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={{ y: -12, color: "#00F0FF" }}
                                className="cursor-default text-white/[0.07] transition-colors"
                            >
                                {letter}
                            </motion.span>
                        ))}
                    </div>
                </div>

                <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/[0.06] pt-8 sm:flex-row">
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500" data-testid="footer-copyright">
                        © 2026 Syntera Technologies Pvt. Ltd. All rights reserved.
                    </p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        syntera.co.in, Bengaluru India
                    </p>
                </div>
            </div>
        </footer>
    );
};
